import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Box,
  Chip as MuiChip,
  Divider,
  Grid2 as Grid,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { green, yellow } from '@mui/material/colors'
import { spacing, SpacingProps } from '@mui/system'
import { isEmpty, round } from 'lodash'
import { useEffect, useState } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_REVENUE_AND_FUEL_STATS,
  GetPosRevenueAndFuelStatsQuery,
  GetPosRevenueAndFuelStatsQueryVariables,
  PosRevenueAndFuelFilter,
  PosRevenueAndFuelOrdering,
} from '@/graphql'
import { useTablePageState } from '@/hooks/useTablePageState'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'

import DeliciousTable, { HeadCell } from '../../components/DeliciousTable'

export interface ChipProps extends SpacingProps {
  status: 'active' | 'inactive'
}

export const Chip = styled(MuiChip)<ChipProps>`
  ${spacing};
  background: ${props =>
    props.status === 'inactive' ? yellow[700] : green[700]};
  color: ${props =>
    props.status === 'active' && props.theme.palette.common.white};
`

function StoreRevFuelTable({
  showHeader = true,
  filters,
}: {
  showHeader?: boolean
  filters: PosRevenueAndFuelFilter
}) {
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const { t } = useT()

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })

  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [orderBy, setOrderBy] =
    useState<keyof PosRevenueAndFuelOrdering>('name')

  const [fetchStats, { data, loading, error, refetch, variables }] =
    useLazyQuery<
      GetPosRevenueAndFuelStatsQuery,
      GetPosRevenueAndFuelStatsQueryVariables
    >(GET_POS_REVENUE_AND_FUEL_STATS, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchStats({
        variables: {
          filters,
          order: { [orderBy]: order },
          pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
        },
      })
    }
  }, [page, rowsPerPage, orderBy, order, filters])

  const handleRequestSort = (property: keyof PosRevenueAndFuelOrdering) => {
    setPage(0)
    const isAsc = orderBy === property && order === 'ASC'
    setOrder(isAsc ? 'DESC' : 'ASC')
    setOrderBy(property)
  }

  const handleRefresh = () => {
    if (!isEmpty(variables)) {
      refetch()
    }
  }
  useRefresh(handleRefresh)

  const headCells: HeadCell<PosRevenueAndFuelOrdering>[] = [
    {
      id: 'name',
      alignment: 'left',
      label: t('storesReportTable.name'),
      sortKey: 'name',
    },
    {
      id: 'totalRevenue',
      alignment: 'left',
      label: t('storesReportTable.totalRevenue'),
      sortKey: 'totalRevenue',
    },
    {
      id: 'insideSales',
      alignment: 'left',
      label: t('storesReportTable.insideSales'),
      sortKey: 'insideSales',
    },
    {
      id: 'outsideSales',
      alignment: 'left',
      label: t('storesReportTable.outsideSales'),
      sortKey: 'outsideSales',
    },
    {
      id: 'fuelVolume',
      alignment: 'left',
      label: t('storesReportTable.fuelVolume'),
      sortKey: 'fuelVolume',
    },
  ]

  const renderRow = (
    store: GetPosRevenueAndFuelStatsQuery['posRevenueAndFuelStats']['results'][0],
    index: number,
    onRowClick: (storeId: string) => void,
  ) => {
    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={`${store.storeId}-${index}`}
        onClick={() => store.storeId && onRowClick(store.storeId)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell>
          <Box ml={3}>{store.name || store.storeId}</Box>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {store.totalRevenue === null || store.totalRevenue === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(store.totalRevenue))}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {store?.insideSales === null || store?.insideSales === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(store.insideSales))}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {store?.outsideSales === null || store?.outsideSales === undefined
              ? t('storesReportTable.noData')
              : formatCurrency(round(store.outsideSales))}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>
            {store?.fuelVolume === null || store?.fuelVolume === undefined
              ? t('storesReportTable.noData')
              : `${Math.round(store?.fuelVolume || 0).toLocaleString()}`}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <Stack spacing={4}>
        {showHeader && (
          <>
            <Grid justifyContent="space-between" container spacing={10}>
              <Typography variant="h3" gutterBottom display="inline">
                {t('storesReportTable.stores')}
              </Typography>
            </Grid>
            <Divider />
          </>
        )}
        <Grid container spacing={6}>
          <Grid size={12}>
            <DeliciousTable<
              GetPosRevenueAndFuelStatsQuery['posRevenueAndFuelStats']['results'][0],
              PosRevenueAndFuelOrdering
            >
              data={data?.posRevenueAndFuelStats.results || []}
              headCells={headCells}
              renderRow={(store, index) =>
                renderRow(store, index, storeId =>
                  handleNavTo(`/stores/${storeId}/revenue`),
                )
              }
              order={order === 'ASC' ? 'asc' : 'desc'}
              orderBy={orderBy}
              handleRequestSort={handleRequestSort}
              loading={loading}
              error={error?.message}
              totalCount={data?.posRevenueAndFuelStats.totalCount || 0}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              title={t('storesReportTable.stores')}
            />
          </Grid>
        </Grid>
      </Stack>
    </>
  )
}

export default StoreRevFuelTable
